import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Modal, Spinner } from "react-bootstrap";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Loans
 *
 * Tracks bank loans and asset financing — principal, interest rate, payment
 * schedule, outstanding balance, next payment due. Each loan can record
 * repayments which the backend should split into principal vs interest.
 *
 * Backend dependency: ApiConfig.loanList / loanCreate / loanUpdate /
 * loanRepayment.
 */
const Loans = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    loan_name: "",
    lender: "",
    principal_amount: "",
    interest_rate: "",
    start_date: "",
    end_date: "",
    payment_frequency: "monthly",
    monthly_payment: "",
    outstanding_balance: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Repayment recording modal
  const [showRepayModal, setShowRepayModal] = useState(false);
  const [repayLoan, setRepayLoan] = useState(null);
  const [repay, setRepay] = useState({
    repayment_date: "",
    principal_paid: "",
    interest_paid: "",
    note: "",
  });
  const [repaying, setRepaying] = useState(false);

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await simplePostCall(
        ApiConfig.loanList,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          language: "en",
        }),
      );
      setRows(res?.result && Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Loan list fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
  }, [fetchLoans]);

  const totals = rows.reduce(
    (acc, r) => {
      acc.principal += parseFloat(r.principal_amount) || 0;
      acc.outstanding += parseFloat(r.outstanding_balance) || 0;
      return acc;
    },
    { principal: 0, outstanding: 0 },
  );

  const openCreate = () => {
    setEditing(null);
    setForm({
      loan_name: "",
      lender: "",
      principal_amount: "",
      interest_rate: "",
      start_date: "",
      end_date: "",
      payment_frequency: "monthly",
      monthly_payment: "",
      outstanding_balance: "",
      notes: "",
    });
    setShowModal(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({
      loan_name: r.loan_name || "",
      lender: r.lender || "",
      principal_amount: r.principal_amount || "",
      interest_rate: r.interest_rate || "",
      start_date: r.start_date ? String(r.start_date).slice(0, 10) : "",
      end_date: r.end_date ? String(r.end_date).slice(0, 10) : "",
      payment_frequency: r.payment_frequency || "monthly",
      monthly_payment: r.monthly_payment || "",
      outstanding_balance: r.outstanding_balance || "",
      notes: r.notes || "",
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveLoan = async () => {
    if (!form.loan_name || !form.principal_amount) {
      toast.warning("Loan name and principal amount are required.");
      return;
    }
    setSaving(true);
    try {
      const url = editing ? ApiConfig.loanUpdate : ApiConfig.loanCreate;
      const payload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        ...form,
        principal_amount: Number(form.principal_amount),
        interest_rate: form.interest_rate ? Number(form.interest_rate) : 0,
        monthly_payment: form.monthly_payment ? Number(form.monthly_payment) : 0,
        outstanding_balance: form.outstanding_balance
          ? Number(form.outstanding_balance)
          : Number(form.principal_amount),
        ...(editing ? { loan_id: editing.loan_id } : {}),
      };
      const res = await simplePostCall(url, JSON.stringify(payload));
      if (res?.result) {
        toast.success(editing ? "Loan updated." : "Loan created.");
        setShowModal(false);
        fetchLoans();
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch (err) {
      toast.error("Error saving loan.");
    } finally {
      setSaving(false);
    }
  };

  const openRepayModal = (loan) => {
    setRepayLoan(loan);
    setRepay({
      repayment_date: new Date().toISOString().slice(0, 10),
      principal_paid: "",
      interest_paid: "",
      note: "",
    });
    setShowRepayModal(true);
  };

  const submitRepayment = async () => {
    const principal = Number(repay.principal_paid) || 0;
    const interest = Number(repay.interest_paid) || 0;
    if (principal + interest <= 0) {
      toast.warning("Enter at least one of principal or interest paid.");
      return;
    }
    setRepaying(true);
    try {
      const res = await simplePostCall(
        ApiConfig.loanRepayment,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          loan_id: repayLoan.loan_id,
          repayment_date: repay.repayment_date,
          principal_paid: principal,
          interest_paid: interest,
          note: repay.note,
        }),
      );
      if (res?.result) {
        toast.success("Repayment recorded.");
        setShowRepayModal(false);
        fetchLoans();
      } else {
        toast.error(res?.message || "Failed to record repayment.");
      }
    } catch (err) {
      toast.error("Error recording repayment.");
    } finally {
      setRepaying(false);
    }
  };

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <div className="special-header panel-white-all d-flex justify-content-between align-items-center">
          <h4 className="page-title">Loans</h4>
          <Button
            onClick={openCreate}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            + Add Loan
          </Button>
        </div>

        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Principal</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.principal)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding Balance</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
        </div>

        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Loan</th>
                <th>Lender</th>
                <th>Principal</th>
                <th>Interest %</th>
                <th>Frequency</th>
                <th>Outstanding</th>
                <th>Start</th>
                <th>End</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No loans recorded.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.loan_id || idx}>
                    <td>{idx + 1}</td>
                    <td>{r.loan_name}</td>
                    <td>{r.lender || "—"}</td>
                    <td>{customer_currency} {formatNumber(r.principal_amount)}</td>
                    <td>{r.interest_rate ? `${r.interest_rate}%` : "—"}</td>
                    <td>{r.payment_frequency || "—"}</td>
                    <td className="fw-bold">
                      {customer_currency} {formatNumber(r.outstanding_balance)}
                    </td>
                    <td>
                      {r.start_date
                        ? new Date(r.start_date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      {r.end_date
                        ? new Date(r.end_date).toLocaleDateString()
                        : "—"}
                    </td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        className="me-1"
                        onClick={() => openRepayModal(r)}
                        style={{ backgroundColor: "var(--color-primary)", border: "none" }}
                      >
                        Record Repayment
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => openEdit(r)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Create / edit loan modal */}
      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Loan" : "Add Loan"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Loan Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  name="loan_name"
                  value={form.loan_name}
                  onChange={handleFormChange}
                  placeholder="e.g. School bus financing 2024"
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Lender</Form.Label>
                <Form.Control
                  name="lender"
                  value={form.lender}
                  onChange={handleFormChange}
                  placeholder="e.g. KCB Bank"
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Principal <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="principal_amount"
                  value={form.principal_amount}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Interest Rate (%)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="interest_rate"
                  value={form.interest_rate}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Outstanding Balance</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="outstanding_balance"
                  value={form.outstanding_balance}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  name="payment_frequency"
                  value={form.payment_frequency}
                  onChange={handleFormChange}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Per-period Payment</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="monthly_payment"
                  value={form.monthly_payment}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="notes"
                  value={form.notes}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={saveLoan}
            disabled={saving}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {saving ? <Spinner size="sm" /> : editing ? "Save Changes" : "Create Loan"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Repayment modal */}
      <Modal
        show={showRepayModal}
        onHide={() => !repaying && setShowRepayModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Record Repayment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {repayLoan && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa" }}>
              <div><b>{repayLoan.loan_name}</b></div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                {repayLoan.lender} • Outstanding {customer_currency}{" "}
                {formatNumber(repayLoan.outstanding_balance)}
              </div>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Repayment Date</Form.Label>
            <Form.Control
              type="date"
              value={repay.repayment_date}
              onChange={(e) =>
                setRepay({ ...repay, repayment_date: e.target.value })
              }
            />
          </Form.Group>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Principal Paid</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={repay.principal_paid}
                  onChange={(e) =>
                    setRepay({ ...repay, principal_paid: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Interest Paid</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={repay.interest_paid}
                  onChange={(e) =>
                    setRepay({ ...repay, interest_paid: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mt-3">
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={repay.note}
              onChange={(e) => setRepay({ ...repay, note: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowRepayModal(false)}
            disabled={repaying}
          >
            Cancel
          </Button>
          <Button
            onClick={submitRepayment}
            disabled={repaying}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {repaying ? <Spinner size="sm" /> : "Record Repayment"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Loans;
